/**
 * **窗口自己的视图状态** —— 缩放 / 网格 / 吸附开关。
 *
 * ## 为什么这部分**不**跟着文档走（而页面数据跟着走）
 *
 * 用户那句「页面编辑器的数据跟随节点图的节点」说的是**页面数据**：
 * 纸张多大、组件在哪。那确实是文档的一部分，改了就进历史、能撤销、能存盘。
 *
 * 而"这个窗口现在放大到 300%"不是文档的一部分：
 * 它和 `areaViewports`（节点图每个窗口各记一份视口）是同一类东西 ——
 * **视图**。存进文档的后果很具体：A 窗口放大看细节，B 窗口跟着一起放大；
 * 撤销一步会把别人的缩放也撤掉。
 *
 * 所以：**数据归文档，视图归窗口**。判据是「换一个窗口看，它该不该变」。
 */
import { reactive } from 'vue'

/**
 * **这个窗口在看什么。**
 *
 * | 值 | 是什么 |
 * |---|---|
 * | `edit` | 编辑视图：可拖可改，带标尺 / 网格 / 吸附参考线 |
 * | `preview` | **精确预览**：把 IR（绘制指令）画出来 —— 纯黑白、几何按整数点吸附 |
 *
 * ⚠️ 架构文档 §1.3 把这件事说成一条要求：**「编辑视图 / 精确预览 / 实际打印结果」
 * 必须显性分开** —— 矢量灰度预览和 203dpi 1-bit 单色在物理上不可能一样，
 * 混在一起只会让人以为"预览对了打印就会对"。
 * 第三档（实际打印结果）要真机出纸，所以在设计器里只能到"精确预览"；
 * 权威光栅由 Skia 那一侧出（§4.2 的绘制层）。
 */
export type PrintViewMode = 'edit' | 'preview'

export interface PrintViewState {
  /** 在看什么（见 `PrintViewMode`） */
  mode: PrintViewMode
  /** 缩放：数值，或 `'fit'`（跟着窗口大小自适应） */
  zoom: number | 'fit'
  /** 网格步长（mm）；0 = 不画也不吸 */
  gridMm: number
  /** 吸附（网格 + 对齐参考线）开关 */
  snap: boolean
  /** 标尺开关 */
  rulers: boolean
}

/**
 * 默认网格 1mm。
 *
 * ⚠️ 以前是 5mm —— 标签上一条线常常只差 1–2mm，5mm 的吸附会让
 * "想挪一点点"变成"跳一大格"（真机反馈："默认应该按网格宽度自动吸附，
 * 但节点上应该支持更详细的修改"）。吸附**开着**，格子**细**：
 * 想精确就在面板/节点里填数字（那里不吸附）。
 */
export const DEFAULT_GRID_MM = 1
export const GRID_CHOICES: readonly number[] = [0, 1, 2, 5, 10, 20]

/** 吸附阈值（屏幕像素）—— 手感是像素级的，换算成 mm 时要用缩放 */
export const SNAP_PX = 6

const states = new Map<string, PrintViewState>()

/**
 * 某个窗口的视图状态（按需创建，同一个 Area 的编辑器与面板共享同一份）。
 *
 * ⚠️ 面板和编辑器主体是**两个不相邻的插槽**（宿主分别渲染），
 * 靠 props 互相传值是做不到的 —— 所以共享状态只能放在包自己这里，
 * 用窗口 id 当键（和夹具包同一个做法，见 `node-packages.md` §6.11）。
 */
export function viewStateOf(areaId: string): PrintViewState {
  const existing = states.get(areaId)
  if (existing) return existing
  const fresh = reactive<PrintViewState>({
    mode: 'edit',
    zoom: 'fit',
    gridMm: DEFAULT_GRID_MM,
    snap: true,
    rulers: true,
  })
  states.set(areaId, fresh)
  return fresh
}

/** 视图状态的表（验证里要断言"关掉窗口不留状态"时用得到） */
export function viewStates(): ReadonlyMap<string, PrintViewState> {
  return states
}
